import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search, X, ChevronLeft, ChevronRight, Music2, ChevronDown, Users, Zap, Calendar, Flame, Disc3, ListMusic } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ReleaseModal from "../components/ReleaseModal";
import { CoverImage } from "../components/ReleaseCard";
import { formatDate } from "../utils/affiliateHelper";
import PartnerSpot from "../components/PartnerSpot";
import api from "../utils/api";
import { useTopHype } from "../context/TopHypeContext";

const TYPES = ["Single", "EP", "Album", "Compilation", "CD"];
const YEARS = (() => {
  const ys = [];
  for (let y = new Date().getFullYear(); y >= 1998; y--) ys.push(String(y));
  return ys;
})();
const LIMIT = 24;

/* ── Kommende Releases Sektion ── */
const DE_MONTHS = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];

function groupByMonth(releases) {
  const groups = {};
  for (const r of releases) {
    const d = r.erscheinungsdatum || "";
    const key = d.slice(0, 7); // "YYYY-MM"
    if (!groups[key]) groups[key] = [];
    groups[key].push(r);
  }
  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
}

function formatMonthLabel(key) {
  const [year, month] = key.split("-");
  return `${DE_MONTHS[parseInt(month, 10) - 1]} ${year}`;
}

function UpcomingReleasesSection({ onSelect }) {
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    api.get("/releases/upcoming?limit=50")
      .then(res => setUpcoming(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="mb-8">
      <div className="h-5 w-48 bg-zinc-800 rounded animate-pulse mb-4" />
      <div className="space-y-2">
        {[...Array(4)].map((_, i) => <div key={i} className="h-14 rounded-xl bg-zinc-900 animate-pulse" />)}
      </div>
    </div>
  );

  if (!upcoming.length) return null;

  const grouped = groupByMonth(upcoming);

  return (
    <div className="mb-10" data-testid="upcoming-releases-section">
      <button
        onClick={() => setCollapsed(c => !c)}
        className="flex items-center gap-2.5 mb-5 group w-full text-left"
        data-testid="upcoming-toggle-btn"
      >
        <Calendar size={15} className="text-rose-500" />
        <span className="text-white font-black text-lg font-heading">Kommende Releases</span>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-rose-600/15 text-rose-400 border border-rose-500/20">
          {upcoming.length}
        </span>
        <ChevronDown
          size={14}
          className={`ml-auto text-zinc-600 group-hover:text-zinc-400 transition-all ${collapsed ? "-rotate-90" : ""}`}
        />
      </button>

      {!collapsed && (
        <div className="space-y-6">
          {grouped.map(([monthKey, releases]) => (
            <div key={monthKey}>
              <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2.5 flex items-center gap-2">
                <span className="h-px flex-1 bg-white/5" />
                {formatMonthLabel(monthKey)}
                <span className="h-px flex-1 bg-white/5" />
              </p>
      <div className="rounded-2xl bg-zinc-900/30 overflow-hidden divide-y divide-white/[0.03] shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]">
                {releases.map(r => (
                  <button
                    key={r.id}
                    onClick={() => onSelect(r)}
                    data-testid={`upcoming-release-${r.id}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.04] transition-colors text-left group"
                  >
                    <div className="w-10 h-10 rounded-xl overflow-hidden bg-zinc-800 flex-shrink-0 shadow-sm">
                      {r.cover_url ? (
                        <img src={r.cover_url} alt={r.titel} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Music2 size={14} className="text-zinc-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-200 text-sm font-semibold truncate group-hover:text-white transition-colors">{r.interpret}</p>
                      <p className="text-zinc-500 text-xs truncate">{r.titel}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-zinc-600 text-xs tabular-nums">{r.erscheinungsdatum}</span>
                      {r.typ && (
                        <span className="hidden sm:inline text-xs px-2 py-0.5 rounded-lg bg-white/[0.04] text-zinc-500">
                          {r.typ}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HypeStrip({ onSelect }) {
  const [hypes, setHypes] = useState([]);
  useEffect(() => {
    api.get("/hype/top?limit=3")
      .then(res => setHypes(res.data || []))
      .catch(() => {});
  }, []);
  if (!hypes.length) return null;
  return (
    <div className="mb-7 p-3 sm:p-4 rounded-2xl bg-amber-500/[0.04] shadow-[inset_0_1px_0_rgba(245,158,11,0.08)]" data-testid="hype-strip">
      <div className="flex items-center gap-2 mb-3">
        <Zap size={12} className="text-amber-400" fill="currentColor" />
        <span className="text-amber-400 text-xs font-bold uppercase tracking-widest">Hype diesen Monat</span>
        <Link to="/charts" className="ml-auto text-zinc-600 hover:text-amber-400 text-xs font-semibold transition-colors">
          Alle Charts →
        </Link>
      </div>
      <div className="flex gap-2 flex-wrap">
        {hypes.map((r, i) => (
          <button
            key={r.id}
            onClick={() => onSelect(r)}
            data-testid={`hype-strip-entry-${i + 1}`}
      className="flex items-center gap-2 bg-zinc-900/50 hover:bg-zinc-900/80 rounded-xl px-2 py-1.5 transition-all shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] group"
          >
            <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-zinc-800">
              <CoverImage
                cover_url={r.cover_url}
                interpret={r.interpret}
                titel={r.titel}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-left min-w-0">
              <p className="text-white text-xs font-semibold truncate max-w-[120px] group-hover:text-amber-300 transition-colors">{r.interpret}</p>
              <p className="text-zinc-500 text-xs truncate max-w-[120px]">{r.titel}</p>
            </div>
            <div className="flex items-center gap-0.5 flex-shrink-0 ml-1">
              <Zap size={9} className="text-amber-400" fill="currentColor" />
              <span className="text-amber-400 text-xs font-bold">{r.hype_count}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ReleaseGridCard({ release, onClick }) {
  const navigate = useNavigate();
  const topHypeIds = useTopHype();
  const isTopHyped = release?.id && topHypeIds.has(release.id);
  return (
    <div
      data-testid="search-page-result"
      className="group cursor-pointer"
    >
      <div
      className="relative aspect-square rounded-2xl overflow-hidden bg-zinc-900 mb-2.5 shadow-[0_4px_20px_rgba(0,0,0,0.35)]"
        onClick={() => onClick(release)}
      >
        <CoverImage
          cover_url={release.cover_url}
          interpret={release.interpret}
          titel={release.titel}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        {release.typ && (
          <span className="absolute top-2 left-2 text-xs px-2 py-0.5 rounded-lg font-semibold bg-black/60 text-zinc-300 backdrop-blur-sm">
            {release.typ}
          </span>
        )}
        {isTopHyped && (
          <div
            title="Einer der meistgehypten Releases diesen Monat"
            className="absolute top-2 right-2 flex items-center gap-1 bg-amber-400/90 backdrop-blur-sm text-zinc-900 text-[9px] font-black px-1.5 py-0.5 rounded-md shadow-lg"
          >
            <Flame size={8} strokeWidth={2.5} />
            HYPE
          </div>
        )}
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/release/${release.post_name}`); }}
            className="text-xs px-2.5 py-1 bg-white/15 hover:bg-white/25 text-white rounded-lg backdrop-blur-sm transition-colors"
          >
            Details →
          </button>
        </div>
      </div>
      <p
        className="text-white font-bold text-sm leading-tight truncate group-hover:text-rose-300 transition-colors cursor-pointer"
        onClick={() => onClick(release)}
      >
        {release.interpret}
      </p>
      <p className="text-zinc-500 text-xs truncate">{release.titel}</p>
      {release.matching_tracks?.length > 0 && (
        <p className="text-zinc-600 text-[10px] truncate mt-0.5 flex items-center gap-1">
          <span className="text-rose-600/70">♫</span>
          {release.matching_tracks[0]}
        </p>
      )}
      {release.erscheinungsdatum && (
        <p className="text-zinc-700 text-xs mt-0.5">{release.erscheinungsdatum?.slice(0, 4)}</p>
      )}
    </div>
  );
}

function RemixListItem({ release, onClick }) {
  const navigate = useNavigate();
  return (
    <div
      data-testid="remix-list-item"
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-zinc-900/40 hover:bg-zinc-900/70 cursor-pointer group transition-all shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]"
      onClick={() => onClick(release)}
    >
      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-zinc-800 shadow-sm">
        <CoverImage
          cover_url={release.cover_url}
          interpret={release.interpret}
          titel={release.titel}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-bold truncate group-hover:text-rose-300 transition-colors">
          {release.interpret}
        </p>
        <p className="text-zinc-500 text-xs truncate">{release.titel}</p>
        {release.matching_tracks?.length > 0 && (
          <p className="text-rose-500/60 text-[10px] truncate flex items-center gap-1 mt-0.5">
            <span>♫</span>
            {release.matching_tracks[0]}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {release.typ && (
          <span className="hidden sm:inline text-xs px-2 py-0.5 rounded-lg bg-white/[0.04] text-zinc-600">
            {release.typ}
          </span>
        )}
        <span className="text-zinc-700 text-xs tabular-nums">{release.erscheinungsdatum?.slice(0, 4)}</span>
        <button
          onClick={(e) => { e.stopPropagation(); navigate(`/release/${release.post_name}`); }}
          className="opacity-0 group-hover:opacity-100 text-xs px-2 py-0.5 bg-white/10 hover:bg-white/20 text-zinc-300 rounded-lg transition-all"
        >
          →
        </button>
      </div>
    </div>
  );
}

// Artist result card for search page
function ArtistSearchCard({ artist }) {
  const initials = (artist.name || "?").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
  return (
    <Link
      to={`/artist/${artist.slug}`}
      data-testid="search-artist-result"
      className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-zinc-900/40 hover:bg-zinc-900/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] transition-all group"
    >
      <div className="w-10 h-10 rounded-full bg-rose-900/30 flex items-center justify-center flex-shrink-0 overflow-hidden shadow-sm">
        {artist.bild_url
          ? <img src={artist.bild_url} alt={artist.name} className="w-full h-full rounded-full object-cover" />
          : <span className="text-rose-400 font-black text-xs">{initials}</span>
        }
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-bold text-sm truncate group-hover:text-rose-300 transition-colors">{artist.name}</p>
        {artist.release_count > 0 && (
          <p className="text-zinc-600 text-xs">{artist.release_count} Release{artist.release_count !== 1 ? "s" : ""}</p>
        )}
      </div>
      <span className="text-zinc-700 group-hover:text-zinc-500 text-xs transition-colors">→</span>
    </Link>
  );
}

function SidebarSection({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="pb-4 mb-4 border-b border-white/[0.04]">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full text-left mb-3 group"
      >
        <span className="text-zinc-500 text-xs font-bold uppercase tracking-[0.15em]">{title}</span>
        <ChevronDown size={12} className={`text-zinc-700 group-hover:text-zinc-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && children}
    </div>
  );
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [inputValue, setInputValue] = useState(searchParams.get("q") || "");
  const [typ, setTyp] = useState(searchParams.get("typ") || "");
  const [year, setYear] = useState(searchParams.get("year") || "");
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [artistResults, setArtistResults] = useState([]);
  const [artistsLoading, setArtistsLoading] = useState(false);
  const [resultTab, setResultTab] = useState("all"); // "all" | "releases" | "remixes"
  const [tabCounts, setTabCounts] = useState({ releases: null, remixes: null, all: null });

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.set("search", query);
      if (typ) params.set("typ", typ);
      if (year) params.set("year", year);
      params.set("page", page);
      params.set("limit", LIMIT);
      // Tab-spezifischer search_in Parameter
      if (query) {
        if (resultTab === "releases") params.set("search_in", "interpret");
        else if (resultTab === "remixes") params.set("search_in", "tracklist_only");
      }
      const res = await api.get(`/releases/?${params}`);
      setResults(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [query, typ, year, page, resultTab]);

  // Fetch artist results whenever the search query changes
  useEffect(() => {
    if (!query || query.length < 2) { setArtistResults([]); return; }
    setArtistsLoading(true);
    api.get(`/artists/?search=${encodeURIComponent(query)}&limit=6`)
      .then(res => setArtistResults(res.data.items || res.data || []))
      .catch(() => setArtistResults([]))
      .finally(() => setArtistsLoading(false));
  }, [query]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  // Reset Tab + Tab-Counts wenn Suchbegriff sich ändert
  useEffect(() => {
    setResultTab("all");
    setTabCounts({ releases: null, remixes: null, all: null });
    if (!query) return;
    // Lade Gesamtzahlen für beide Tabs parallel (mit limit=1 für Performance)
    Promise.all([
      api.get(`/releases/?search=${encodeURIComponent(query)}&limit=1&search_in=interpret`),
      api.get(`/releases/?search=${encodeURIComponent(query)}&limit=1&search_in=tracklist_only`),
      api.get(`/releases/?search=${encodeURIComponent(query)}&limit=1`),
    ]).then(([r, t, all]) => {
      setTabCounts({ releases: r.data.total ?? 0, remixes: t.data.total ?? 0, all: all.data.total ?? 0 });
    }).catch(() => {});
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  // Kein client-seitiges Splitting mehr – Backend liefert korrekte Daten je Tab
  const hasRemixes = tabCounts.remixes !== null && tabCounts.remixes > 0;

  // Titel wird per Helmet gesetzt (s.u. im return)

  useEffect(() => {
    const p = {};
    if (query) p.q = query;
    if (typ) p.typ = typ;
    if (year) p.year = year;
    if (page > 1) p.page = page;
    setSearchParams(p, { replace: true });
  }, [query, typ, year, page, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    setQuery(inputValue.trim());
    setPage(1);
  };

  const clearFilter = (key) => {
    if (key === "typ") setTyp("");
    if (key === "year") setYear("");
    if (key === "query") { setQuery(""); setInputValue(""); }
    setPage(1);
  };

  const totalPages = Math.ceil(total / LIMIT);
  const activeFilters = [
    query && { key: "query", label: `"${query}"` },
    typ && { key: "typ", label: typ },
    year && { key: "year", label: year },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Helmet>
        <title>{query ? `„${query}" – Musik | HUWND` : "Musik – HUWND"}</title>
        <meta name="description" content={query ? `Suchergebnisse für „${query}" in der HUWND Releasedatenbank.` : "Durchsuche die komplette Hands-Up-Releasedatenbank – Singles, EPs, Alben und mehr."} />
        <link rel="canonical" href="https://handsupwillneverdie.de/search" />
        <meta property="og:title" content={query ? `„${query}" – Musik | HUWND` : "Musik – HUWND"} />
        <meta property="og:site_name" content="HUWND – Hands Up Will Never Die" />
        <meta property="og:url" content="https://handsupwillneverdie.de/search" />
      </Helmet>
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Page title */}
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-black font-heading mb-2">{t("search.title")}</h1>
          <p className="text-zinc-600 text-sm">
            {total > 0
              ? `${total.toLocaleString()} ${t("search.results")}`
              : "Release-Datenbank"}
          </p>
        </div>

        {/* Hype Strip */}
        <HypeStrip onSelect={setSelectedRelease} />

        <div className="flex gap-8 items-start">
          {/* ── SIDEBAR ── */}
          <aside className="hidden lg:block w-56 flex-shrink-0 sticky top-20">
            {/* Search */}
            <form onSubmit={handleSearch} className="mb-6">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600 pointer-events-none" />
                <input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Suchen…"
                  data-testid="search-page-input"
                  className="w-full bg-zinc-900/50 ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/30 rounded-2xl py-2.5 pl-8 pr-3 text-white text-sm placeholder-zinc-600 outline-none transition-all"
                />
              </div>
            </form>

            {/* Active filters */}
            {activeFilters.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-5">
                {activeFilters.map(f => (
                  <button key={f.key} onClick={() => clearFilter(f.key)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-xl bg-rose-600/10 text-rose-400 text-xs font-semibold hover:bg-rose-600/20 transition-colors">
                    {f.label} <X size={9} />
                  </button>
                ))}
              </div>
            )}

            {/* Type filter */}
            <SidebarSection title={t("search.filter_type")} defaultOpen={true}>
              <div className="flex flex-col gap-0.5">
                {TYPES.map(t => (
                  <button key={t} onClick={() => { setTyp(typ === t ? "" : t); setPage(1); }}
                    className={`text-left px-3 py-2 rounded-xl text-sm transition-all duration-150 font-medium ${typ === t ? "bg-rose-600/15 text-rose-300" : "text-zinc-500 hover:text-white hover:bg-white/[0.04]"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </SidebarSection>

            {/* Year filter */}
            <SidebarSection title={t("search.filter_year")} defaultOpen={true}>
              <div className="max-h-52 overflow-y-auto scrollbar-thin pr-1 flex flex-col gap-0.5">
                {YEARS.map(y => (
                  <button key={y} onClick={() => { setYear(year === y ? "" : y); setPage(1); }}
                    className={`text-left px-3 py-1.5 rounded-xl text-sm transition-all duration-150 ${year === y ? "bg-rose-600/15 text-rose-300 font-semibold" : "text-zinc-600 hover:text-white hover:bg-white/[0.04]"}`}>
                    {y}
                  </button>
                ))}
              </div>
            </SidebarSection>

            {/* Partner Spot */}
            <PartnerSpot position="search" />
          </aside>

          {/* ── MAIN GRID ── */}
          <main className="flex-1 min-w-0">
            {/* Mobile search bar */}
            <form onSubmit={handleSearch} className="flex gap-2 mb-5 lg:hidden">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600 pointer-events-none" />
                <input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Suchen…"
                  className="w-full bg-zinc-900/50 ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/30 rounded-2xl py-2.5 pl-8 pr-3 text-white text-sm placeholder-zinc-600 outline-none transition-all"
                />
              </div>
              <button type="submit" data-testid="search-page-submit"
                className="px-4 py-2.5 bg-rose-600 hover:bg-rose-500 text-white text-sm font-bold rounded-2xl shadow-[0_4px_12px_rgba(225,29,72,0.2)] transition-all hover:-translate-y-0.5">
                {t("common.search")}
              </button>
            </form>

            {/* Mobile filters strip */}
            <div className="flex flex-wrap gap-1.5 mb-5 lg:hidden">
              {TYPES.map(t => (
                <button key={t} onClick={() => { setTyp(typ === t ? "" : t); setPage(1); }}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${typ === t ? "bg-rose-600/15 text-rose-300 shadow-[inset_0_1px_0_rgba(225,29,72,0.1)]" : "bg-zinc-900/50 text-zinc-400 hover:text-white hover:bg-zinc-900/80"}`}>
                  {t}
                </button>
              ))}
            </div>

            {/* ── Kommende Releases (nur ohne aktive Suche/Filter) ── */}
            {!query && !typ && !year && (
              <UpcomingReleasesSection onSelect={setSelectedRelease} />
            )}

            {/* ── Artist Results ── */}
            {query && query.length >= 2 && (artistsLoading || artistResults.length > 0) && (
              <div className="mb-7">
                <div className="flex items-center gap-2 mb-3">
                  <Users size={14} className="text-zinc-500" />
                  <span className="text-zinc-400 text-xs font-bold uppercase tracking-widest">Künstler</span>
                  {artistsLoading && <span className="text-zinc-700 text-xs">Lädt…</span>}
                </div>
                {artistsLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-16 rounded-xl bg-zinc-900 animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="search-artists-list">
                    {artistResults.map(a => <ArtistSearchCard key={a.id} artist={a} />)}
                  </div>
                )}
              </div>
            )}

            {/* ── Release/Remix Tabs (nur bei aktiver Suche mit Remixes) ── */}
            {query && !loading && hasRemixes && (
              <div className="flex items-center gap-1 mb-5" data-testid="result-tabs">
                {[
                  { key: "all", label: "Alles", icon: <Music2 size={12} />, count: tabCounts.all ?? total },
                  { key: "releases", label: "Releases", icon: <Disc3 size={12} />, count: tabCounts.releases },
                  { key: "remixes", label: "Remixes", icon: <ListMusic size={12} />, count: tabCounts.remixes },
                ].map(tab => (
                  <button
                    key={tab.key}
                    data-testid={`result-tab-${tab.key}`}
                    onClick={() => { setResultTab(tab.key); setPage(1); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                      resultTab === tab.key
                        ? "bg-rose-600/15 text-rose-300 shadow-[inset_0_1px_0_rgba(225,29,72,0.1)]"
                        : "text-zinc-500 hover:text-white hover:bg-white/[0.04]"
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                    {tab.count !== null && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        resultTab === tab.key ? "bg-rose-600/20 text-rose-400" : "bg-white/[0.04] text-zinc-600"
                      }`}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Grid */}
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4">
                {[...Array(LIMIT)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <div className="aspect-square rounded-xl bg-zinc-900 animate-pulse" />
                    <div className="h-3 bg-zinc-900 rounded animate-pulse w-4/5" />
                    <div className="h-3 bg-zinc-900 rounded animate-pulse w-3/5" />
                  </div>
                ))}
              </div>
            ) : results.length > 0 ? (
              resultTab === "remixes" ? (
                /* ── Remix-Liste ── */
                <div className="space-y-1" data-testid="remix-results-list">
                  {results.map(r => (
                    <RemixListItem key={r.id} release={r} onClick={setSelectedRelease} />
                  ))}
                </div>
              ) : (
                /* ── Releases-Grid ── */
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="search-results-list">
                  {results.map(r => (
                    <ReleaseGridCard key={r.id} release={r} onClick={setSelectedRelease} />
                  ))}
                </div>
              )
            ) : (
              <div className="py-24 text-center">
                <Music2 size={40} className="mx-auto mb-3 text-zinc-800" />
                <p className="text-zinc-500 text-sm font-medium">
                  {query || typ || year ? "Keine Ergebnisse für diese Filter" : "Keine Releases gefunden"}
                </p>
                {(query || typ || year) && (
                  <button onClick={() => { setQuery(""); setInputValue(""); setTyp(""); setYear(""); setPage(1); }}
                    className="mt-3 text-xs text-rose-400 hover:text-rose-300 transition-colors">
                    Filter zurücksetzen
                  </button>
                )}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-10 pt-6 border-t border-white/[0.04]">
                <button
                  onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  disabled={page === 1}
                  data-testid="search-prev-page"
                  className="flex items-center gap-2 px-4 py-2.5 bg-zinc-900/50 hover:bg-zinc-900/80 text-zinc-300 text-sm font-semibold rounded-2xl disabled:opacity-30 disabled:cursor-not-allowed shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] transition-all duration-200"
                >
                  <ChevronLeft size={14} /> Zurück
                </button>
                <span className="text-zinc-600 text-sm tabular-nums">
                  <strong className="text-zinc-400">{page}</strong> / {totalPages}
                </span>
                <button
                  onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  disabled={page === totalPages}
                  data-testid="search-next-page"
                  className="flex items-center gap-2 px-4 py-2.5 bg-zinc-900/50 hover:bg-zinc-900/80 text-zinc-300 text-sm font-semibold rounded-2xl disabled:opacity-30 disabled:cursor-not-allowed shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] transition-all duration-200"
                >
                  Weiter <ChevronRight size={14} />
                </button>
              </div>
            )}
          </main>
        </div>
      </div>

      <Footer />

      {selectedRelease && (
        <ReleaseModal release={selectedRelease} onClose={() => setSelectedRelease(null)} />
      )}
    </div>
  );
}
